import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c502Api, PendingItem, PendingResponse, ApproveResponse, ApprovalFilter } from '../../../services/c502Api';

export interface UseC502ApprovalReturn {
  // State
  loading: boolean;
  approving: boolean;
  pendingItems: PendingItem[];
  total: number;
  userLevel: string;
  selectedKeys: string[];
  filter: ApprovalFilter;
  lastApiResponse: unknown;

  // Actions
  setFilter: (f: ApprovalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
}

export function useC502Approval(): UseC502ApprovalReturn {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [userLevel, setUserLevel] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ApprovalFilter>({ scStart: '', scEnd: '' });
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await c502Api.getPending(filter);
      setLastApiResponse(resp);
      setPendingItems(resp.items || []);
      setTotal(resp.total || 0);
      setUserLevel(resp.level || '');
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆 S/C 進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await c502Api.approve(selectedKeys);
      message.success(resp.message);
      // Refresh list after approval
      await fetchPending();
      setLastApiResponse(resp);
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    pendingItems,
    total,
    userLevel,
    selectedKeys,
    filter,
    lastApiResponse,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
  };
}
